<template>
  <div class="w-full bg-gray-50 border-b border-gray-200">
    <div class="w-full px-6 py-3">
      <div class="flex items-center">
        <!-- Left: Title + date context -->
        <div class="flex items-center">
          <h1 class="text-lg font-semibold text-gray-900 mr-6">Schedule</h1>

          <!-- Date navigation -->
          <div class="flex items-center bg-white rounded-lg border border-gray-200" style="height:40px">
            <button @click="previousDate()"
              class="w-10 h-full flex items-center justify-center rounded-l-lg hover:bg-gray-100 transition-colors text-gray-600">
              <i class="fa-solid fa-chevron-left text-sm"></i>
            </button>

            <button @click="goToToday()"
              class="px-3 h-full text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors border-l border-r border-gray-200">
              Today
            </button>

            <button @click="nextDate()"
              class="w-10 h-full flex items-center justify-center rounded-r-lg hover:bg-gray-100 transition-colors text-gray-600">
              <i class="fa-solid fa-chevron-right text-sm"></i>
            </button>
          </div>

          <!-- Primary date display -->
          <div class="ml-4 flex items-center">
            <datetime
              v-model="prevDate"
              type="date"
              input-class="bg-transparent text-sm font-semibold text-gray-800 cursor-pointer outline-none w-40 hover:text-primary transition-colors"
              class="theme-orange"
              style="height:40px;display:flex;align-items:center"
            ></datetime>

            <!-- Range toggle -->
            <button @click="showRange = !showRange"
              class="ml-2 px-3 rounded-lg text-xs font-medium transition-colors flex items-center border"
              style="height:34px"
              :class="showRange ? 'bg-primary text-white border-primary' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-100'">
              <i class="fa-solid fa-arrow-right-long mr-1.5" style="font-size:10px"></i>Range
            </button>

            <!-- To date (only shown when range toggled) -->
            <div v-if="showRange" class="flex items-center ml-2">
              <span class="text-xs text-gray-400 mr-2">to</span>
              <datetime
                v-model="date"
                type="date"
                input-class="bg-transparent text-sm font-semibold text-gray-800 cursor-pointer outline-none w-40 hover:text-primary transition-colors"
                class="theme-orange"
                style="height:40px;display:flex;align-items:center"
              ></datetime>
            </div>
          </div>

          <!-- Day label -->
          <span class="ml-3 text-sm text-gray-400 font-normal hidden lg:inline">{{ dayLabel }}</span>
        </div>

        <!-- Right: Actions -->
        <div class="ml-auto flex items-center space-x-2">
          <button @click="openMapView()"
            class="px-4 rounded-lg text-sm font-medium flex items-center bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors"
            style="height:40px">
            <i class="fa-solid fa-map-location-dot mr-2" style="font-size:12px"></i>View Map
          </button>

          <button v-if="currentUser.role === 'admin'" @click="openBulkSms()"
            class="px-4 rounded-lg text-sm font-medium inline-flex items-center whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            style="height:40px">
            <i class="fa-solid fa-comments mr-2" style="font-size:12px"></i>Bulk SMS
          </button>

          <button v-if="currentUser.role === 'admin'" @click="openStartTimes()"
            class="px-4 rounded-lg text-sm font-medium inline-flex items-center whitespace-nowrap bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            style="height:40px">
            <i class="fa-solid fa-clock mr-2" style="font-size:12px"></i>Start Times
          </button>

          <button @click="toggleExport()"
            class="px-4 rounded-lg text-sm font-medium flex items-center bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors"
            style="height:40px">
            <i class="fa-solid fa-arrow-up-from-bracket mr-2"></i>Export
          </button>

          <router-link v-if="currentUser.role == 'admin'" :to="{name: 'Jobs'}"
            class="px-4 rounded-lg text-sm font-medium flex items-center bg-primary text-white hover:opacity-90 transition-colors"
            style="height:40px">
            <i class="fa-solid fa-plus mr-2" style="font-size:12px"></i>Add Job
          </router-link>

          <button @click="logout()"
            class="px-3 rounded-lg text-sm font-medium flex items-center bg-white text-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            style="height:40px"
            title="Logout">
            <i class="fa-solid fa-right-from-bracket mr-2" style="font-size:12px"></i>Logout
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import { bus } from "../../main";
import { signMeOut } from "../../firestore/utils";

export default {
  props: ['jobs', 'mapView'],
  data() {
    return {
      search: "",
      date: "",
      prevDate: "",
      showRange: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    currentStartDate() {
      return this.$store.state.currentStartDate;
    },
    currentEndDate() {
      return this.$store.state.currentEndDate;
    },
    dayLabel() {
      if (!this.prevDate) return '';
      const d = this.$moment(this.prevDate);
      const today = this.$moment();
      if (d.isSame(today, 'day')) return 'Today';
      if (d.isSame(today.clone().add(1, 'day'), 'day')) return 'Tomorrow';
      if (d.isSame(today.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
      return d.format('dddd');
    },
  },
  watch: {
    search() {
      bus.$emit("search", this.search);
    },
    date(newVal) {
      if (newVal) {
        bus.$emit("changeDate", { prevDate: this.prevDate, date: this.date });
        this.$store.commit("updateState", { state: "currentEndDate", body: this.date });
      }
    },
    prevDate(newVal, oldVal) {
      if (newVal) {
        // If range is off, keep dates synced
        if (!this.showRange) {
          this.date = newVal;
        }
        bus.$emit("changeDate", { prevDate: this.prevDate, date: this.date });
        this.$store.commit("updateState", { state: "currentStartDate", body: this.prevDate });
      }
    },
    showRange(on) {
      if (!on) {
        // Turning off range: sync date to prevDate
        this.date = this.prevDate;
      }
    },
  },
  methods: {
    nextDate() {
      this.date = this.$moment(this.date).add(1, "days").toISOString();
      this.prevDate = this.$moment(this.prevDate).add(1, "days").toISOString();
      this.$store.commit("updateState", { state: "currentStartDate", body: this.prevDate });
      this.$store.commit("updateState", { state: "currentEndDate", body: this.date });
    },
    previousDate() {
      this.date = this.$moment(this.date).subtract(1, "days").toISOString();
      this.prevDate = this.$moment(this.prevDate).subtract(1, "days").toISOString();
      this.$store.commit("updateState", { state: "currentStartDate", body: this.prevDate });
      this.$store.commit("updateState", { state: "currentEndDate", body: this.date });
    },
    goToToday() {
      const today = this.$moment().add(11, "hours").toISOString();
      this.prevDate = today;
      this.date = today;
      this.$store.commit("updateState", { state: "currentStartDate", body: this.prevDate });
      this.$store.commit("updateState", { state: "currentEndDate", body: this.date });
    },
    toggleExport() {
      bus.$emit("triggerExport", true);
    },
    openBulkSms() {
      bus.$emit("openBulkSms");
    },
    openStartTimes() {
      bus.$emit("openStartTimes");
    },
    openMapView() {
      bus.$emit("openMapView");
    },
    toggleView() {
      bus.$emit("toggleMapView");
    },
    async logout() {
      await signMeOut();
      this.$router.push({ name: 'Login' });
    },
  },
  components: {
    datetime: Datetime,
  },
  created() {
    this.date = this.$moment().add(11, "hours").toISOString();
    this.prevDate = this.$moment().add(11, "hours").toISOString();
    if (this.currentStartDate) this.date = this.$moment(this.currentStartDate).add(11, "hours").toISOString();
    if (this.currentEndDate) this.prevDate = this.$moment(this.currentEndDate).add(11, "hours").toISOString();
  },
};
</script>
